// src/components/Admin/GradeReclamations.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Snackbar,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Close as CloseIcon,
  Reply as ReplyIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Room as RoomIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

const STATUS_META = {
  pending:     { label: 'En attente', color: 'warning' },
  in_progress: { label: 'En cours',   color: 'info' },
  resolved:    { label: 'Traitée',    color: 'success' },
  rejected:    { label: 'Rejetée',    color: 'error' },
};

/* ── Helpers ── */
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmtTime = (t) => (t ? t.slice(0, 5) : '—');

const exportToCSV = (rows) => {
  const cols = [
    { key: 'id',              label: '#' },
    { key: 'cod_etu',         label: 'Code étudiant' },
    { key: 'lib_nom_pat_ind', label: 'Nom' },
    { key: 'lib_pr1_ind',     label: 'Prénom' },
    { key: 'cin_ind',         label: 'CIN' },
    { key: 'module_code',     label: 'Code module' },
    { key: 'module_name',     label: 'Nom module' },
    { key: 'academic_year',   label: 'Année' },
    { key: 'semester',        label: 'Semestre' },
    { key: 'reported_grade',  label: 'Note signalée' },
    { key: 'description',     label: 'Motif' },
    { key: 'status',          label: 'Statut' },
    { key: 'exam_date',       label: 'Date examen' },
    { key: 'start_time',      label: 'Heure début' },
    { key: 'end_time',        label: 'Heure fin' },
    { key: 'exam_location',   label: 'Local' },
    { key: 'professor_name',  label: 'Professeur' },
    { key: 'exam_group',      label: 'Groupe' },
    { key: 'admin_response',  label: 'Réponse admin' },
    { key: 'responded_by',    label: 'Traité par' },
    { key: 'created_at',      label: 'Date soumission' },
  ];
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const str = String(v).replace(/\r\n|\r|\n/g, ' ').trim();
    return `"${str.replace(/"/g, '""')}"`;
  };
  const header = cols.map(c => esc(c.label)).join(',');
  const body = rows.map(r =>
    cols.map(c => {
      let v = r[c.key];
      if (c.key === 'exam_date') v = fmtDate(v);
      if (c.key === 'start_time' || c.key === 'end_time') v = fmtTime(v);
      if (c.key === 'created_at') v = v ? new Date(v).toLocaleString('fr-FR') : '';
      return esc(v);
    }).join(',')
  ).join('\n');
  const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `reclamations_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ── Exam slot card (inside details dialog) ── */
const ExamSlotCard = ({ rec }) => {
  const hasSlot = rec.exam_date || rec.exam_location || rec.professor_name;
  if (!hasSlot) return (
    <Alert severity="info" sx={{ mt: 0 }}>
      Aucun créneau d'examen trouvé pour cet étudiant / module dans la session.
    </Alert>
  );
  return (
    <Paper elevation={0} sx={{ border: '1.5px solid #dbeafe', borderRadius: 2, p: 2, bgcolor: '#eff6ff' }}>
      <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
        Informations du créneau d'examen
      </Typography>
      <Grid container spacing={1.5}>
        {rec.exam_date && (
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventIcon fontSize="small" color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">Date</Typography>
                <Typography variant="body2" fontWeight={600}>{fmtDate(rec.exam_date)}</Typography>
              </Box>
            </Box>
          </Grid>
        )}
        {(rec.start_time || rec.end_time) && (
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon fontSize="small" color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">Horaire</Typography>
                <Typography variant="body2" fontWeight={600}>{fmtTime(rec.start_time)} — {fmtTime(rec.end_time)}</Typography>
              </Box>
            </Box>
          </Grid>
        )}
        {rec.exam_location && (
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RoomIcon fontSize="small" color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">Local / Salle</Typography>
                <Typography variant="body2" fontWeight={600}>{rec.exam_location}</Typography>
              </Box>
            </Box>
          </Grid>
        )}
        {rec.professor_name && (
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">Professeur</Typography>
                <Typography variant="body2" fontWeight={600}>{rec.professor_name}</Typography>
              </Box>
            </Box>
          </Grid>
        )}
        {rec.exam_group && (
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, display: 'flex', justifyContent: 'center' }}>
                <Typography variant="body2" color="primary" fontWeight={800}>G</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Groupe</Typography>
                <Typography variant="body2" fontWeight={600}>{rec.exam_group}</Typography>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

/* ══════════════════════════════════════════ */
const GradeReclamations = () => {
  const [reclamations, setReclamations]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [statusFilter, setStatusFilter]   = useState('');
  const [searchTerm, setSearchTerm]       = useState('');

  // Enable/disable grade reclamation submissions for students
  const [reclamationsEnabled, setReclamationsEnabled]                 = useState(true);
  const [rattrapageReclamationsEnabled, setRattrapageReclamationsEnabled] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Bulk status change
  const [bulkStatus, setBulkStatus]   = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  // Delete confirm dialog
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, single: null }); // single = id or null (bulk)

  // Details dialog
  const [selected, setSelected]   = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Respond dialog
  const [respondDialog, setRespondDialog] = useState({ open: false, id: null });
  const [respondStatus, setRespondStatus] = useState('resolved');
  const [respondText, setRespondText]     = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { fetchReclamations(); }, [statusFilter]);

  useEffect(() => {
    adminAPI.getSettings().then(res => {
      const flat = res.flat || [];
      const normale    = flat.find(s => s.key === 'reclamations.grade_reclamations_enabled');
      const rattrapage = flat.find(s => s.key === 'reclamations.rattrapage_grade_reclamations_enabled');
      setReclamationsEnabled(normale ? normale.value === 'true' : true);
      setRattrapageReclamationsEnabled(rattrapage ? rattrapage.value === 'true' : false);
    }).catch(() => {});
  }, []);

  const handleToggleEnabled = async () => {
    const next = !reclamationsEnabled;
    setToggleLoading(true);
    try {
      await adminAPI.updateSettings([{ key: 'reclamations.grade_reclamations_enabled', value: next }]);
      setReclamationsEnabled(next);
      notify(next ? 'Réclamations Normale ouvertes aux étudiants.' : 'Réclamations Normale fermées.');
    } catch (err) {
      notify(err.response?.data?.error || "Erreur lors de la mise à jour.", 'error');
    } finally {
      setToggleLoading(false);
    }
  };

  const handleToggleRattrapageEnabled = async () => {
    const next = !rattrapageReclamationsEnabled;
    setToggleLoading(true);
    try {
      await adminAPI.updateSettings([{ key: 'reclamations.rattrapage_grade_reclamations_enabled', value: next }]);
      setRattrapageReclamationsEnabled(next);
      notify(next ? 'Réclamations Rattrapage ouvertes aux étudiants.' : 'Réclamations Rattrapage fermées.');
    } catch (err) {
      notify(err.response?.data?.error || "Erreur lors de la mise à jour.", 'error');
    } finally {
      setToggleLoading(false);
    }
  };

  const fetchReclamations = async () => {
    try {
      setLoading(true);
      setSelectedIds(new Set());
      const filters = { type: 'grade_reclamation' };
      if (statusFilter) filters.status = statusFilter;
      const res = await adminAPI.getReclamations(filters);
      setReclamations(res.reclamations || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const notify = (message, severity = 'success') => setNotification({ open: true, message, severity });

  /* ── Search filter ── */
  const filteredReclamations = reclamations.filter(rec => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    const haystack = [
      rec.cod_etu, rec.lib_nom_pat_ind, rec.lib_pr1_ind,
      rec.module_code, rec.module_name, rec.description, rec.academic_year, rec.semester,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(term);
  });

  /* ── Selection helpers ── */
  const allSelected = filteredReclamations.length > 0 && selectedIds.size === filteredReclamations.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredReclamations.map(r => r.id)));
    }
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  /* ── Bulk status ── */
  const handleBulkStatus = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedIds);
      await adminAPI.bulkUpdateReclamationStatus(ids, bulkStatus);
      setReclamations(prev => prev.map(r => selectedIds.has(r.id) ? { ...r, status: bulkStatus } : r));
      notify(`Statut mis à jour pour ${ids.length} réclamation(s)`);
      setSelectedIds(new Set());
      setBulkStatus('');
    } catch (err) {
      notify(err.response?.data?.error || 'Erreur', 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    const { single } = deleteConfirm;
    setBulkLoading(true);
    try {
      if (single !== null) {
        await adminAPI.deleteReclamation(single);
        setReclamations(prev => prev.filter(r => r.id !== single));
        setSelectedIds(prev => { const n = new Set(prev); n.delete(single); return n; });
        notify('Réclamation supprimée');
      } else {
        const ids = Array.from(selectedIds);
        await adminAPI.bulkDeleteReclamations(ids);
        setReclamations(prev => prev.filter(r => !selectedIds.has(r.id)));
        setSelectedIds(new Set());
        notify(`${ids.length} réclamation(s) supprimée(s)`);
      }
    } catch (err) {
      notify(err.response?.data?.error || 'Erreur', 'error');
    } finally {
      setBulkLoading(false);
      setDeleteConfirm({ open: false, single: null });
    }
  };

  /* ── Details dialog ── */
  const handleOpenDialog = (rec) => { setSelected(rec); setOpenDialog(true); };
  const handleCloseDialog = () => { setOpenDialog(false); setSelected(null); };

  /* ── Respond dialog ── */
  const openRespondDialog = (rec) => {
    setRespondDialog({ open: true, id: rec.id });
    setRespondStatus(rec.status === 'pending' ? 'in_progress' : rec.status);
    setRespondText(rec.admin_response || '');
    if (openDialog) handleCloseDialog();
  };

  const handleSubmitResponse = async () => {
    try {
      setActionLoading(true);
      const updated = await adminAPI.updateReclamation(respondDialog.id, {
        status: respondStatus,
        admin_response: respondText,
      });
      setReclamations(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
      notify('Réclamation mise à jour avec succès');
      setRespondDialog({ open: false, id: null });
    } catch (err) {
      notify(err.response?.data?.error || 'Erreur', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h4" fontWeight="600" color="primary">
          Réclamations de Notes
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Statut</InputLabel>
            <Select label="Statut" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); }}>
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="in_progress">En cours</MenuItem>
              <MenuItem value="resolved">Traitée</MenuItem>
              <MenuItem value="rejected">Rejetée</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => exportToCSV(reclamations)}
            disabled={reclamations.length === 0}
          >
            Exporter CSV
          </Button>
        </Box>
      </Box>

      {/* ── Enable/disable toggles for student submissions (Normale + Rattrapage) ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Normale */}
        <Paper
          elevation={0}
          sx={{
            flex: 1, minWidth: 260, px: 2.5, py: 1.5, borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
            bgcolor: reclamationsEnabled ? '#f0fdf4' : '#fef2f2',
            border: `1.5px solid ${reclamationsEnabled ? '#bbf7d0' : '#fecaca'}`,
          }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: reclamationsEnabled ? '#166534' : '#991b1b' }}>
              Réclamations Normale
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {reclamationsEnabled ? 'Bouton « Réclamer » visible (session normale).' : 'Bouton « Réclamer » masqué (session normale).'}
            </Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={reclamationsEnabled} onChange={handleToggleEnabled} disabled={toggleLoading} color="success" />}
            label={reclamationsEnabled ? 'Activées' : 'Désactivées'}
            sx={{ m: 0, flexShrink: 0 }}
          />
        </Paper>

        {/* Rattrapage */}
        <Paper
          elevation={0}
          sx={{
            flex: 1, minWidth: 260, px: 2.5, py: 1.5, borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
            bgcolor: rattrapageReclamationsEnabled ? '#fffbeb' : '#fef2f2',
            border: `1.5px solid ${rattrapageReclamationsEnabled ? '#fde68a' : '#fecaca'}`,
          }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: rattrapageReclamationsEnabled ? '#92400e' : '#991b1b' }}>
              Réclamations Rattrapage
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {rattrapageReclamationsEnabled ? 'Bouton « Réclamer » visible (session rattrapage).' : 'Bouton « Réclamer » masqué (session rattrapage).'}
            </Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={rattrapageReclamationsEnabled} onChange={handleToggleRattrapageEnabled} disabled={toggleLoading} color="warning" />}
            label={rattrapageReclamationsEnabled ? 'Activées' : 'Désactivées'}
            sx={{ m: 0, flexShrink: 0 }}
          />
        </Paper>
      </Box>

      {/* ── Search ── */}
      <TextField
        size="small"
        fullWidth
        placeholder="Rechercher par étudiant, code, module ou motif..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
        }}
      />

      {/* ── Bulk action toolbar (appears when rows are selected) ── */}
      {selectedIds.size > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 2, px: 2, py: 1.5, borderRadius: 2,
            bgcolor: '#fef3c7', border: '1.5px solid #fde68a',
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ color: '#92400e' }}>
            {selectedIds.size} sélectionnée(s)
          </Typography>

          <Button
            size="small"
            color="inherit"
            sx={{ color: '#92400e', textTransform: 'none' }}
            onClick={() => setSelectedIds(new Set())}
          >
            Désélectionner tout
          </Button>

          <Divider orientation="vertical" flexItem sx={{ borderColor: '#fcd34d' }} />

          {/* Bulk status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon fontSize="small" sx={{ color: '#92400e' }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Nouveau statut</InputLabel>
              <Select
                label="Nouveau statut"
                value={bulkStatus}
                onChange={e => setBulkStatus(e.target.value)}
                sx={{ bgcolor: 'white' }}
              >
                <MenuItem value="pending">En attente</MenuItem>
                <MenuItem value="in_progress">En cours</MenuItem>
                <MenuItem value="resolved">Traitée</MenuItem>
                <MenuItem value="rejected">Rejetée</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="contained"
              color="warning"
              disabled={!bulkStatus || bulkLoading}
              onClick={handleBulkStatus}
            >
              Appliquer
            </Button>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: '#fcd34d' }} />

          {/* Bulk delete */}
          <Button
            size="small"
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteConfirm({ open: true, single: null })}
            disabled={bulkLoading}
          >
            Supprimer ({selectedIds.size})
          </Button>
        </Paper>
      )}

      {/* ── Table ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead sx={{ bgcolor: '#f8f9fa' }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    disabled={filteredReclamations.length === 0}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Étudiant</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Année / Semestre</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Note signalée</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReclamations.length > 0 ? (
                filteredReclamations.map((rec) => {
                  const isChecked = selectedIds.has(rec.id);
                  return (
                    <TableRow
                      key={rec.id}
                      hover
                      selected={isChecked}
                      sx={{ '&.Mui-selected': { bgcolor: '#fffbeb' }, '&.Mui-selected:hover': { bgcolor: '#fef9c3' } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isChecked} onChange={() => toggleOne(rec.id)} />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>#{rec.id}</TableCell>
                      <TableCell>{`${rec.lib_nom_pat_ind} ${rec.lib_pr1_ind}`}</TableCell>
                      <TableCell>{rec.cod_etu}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{rec.module_name || rec.module_code}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {rec.module_code}
                        </Typography>
                      </TableCell>
                      <TableCell>{rec.academic_year} / {rec.semester}</TableCell>
                      <TableCell>
                        <Chip
                          label={rec.reported_grade === 'AB' ? 'ABS' : (rec.reported_grade ?? '0')}
                          size="small" color="error" sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_META[rec.status]?.label || rec.status}
                          color={STATUS_META[rec.status]?.color || 'default'}
                          size="small" sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>{new Date(rec.created_at).toLocaleDateString()}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="Voir détails">
                            <IconButton color="info" size="small" onClick={() => handleOpenDialog(rec)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Répondre">
                            <IconButton color="primary" size="small" onClick={() => openRespondDialog(rec)}>
                              <ReplyIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => setDeleteConfirm({ open: true, single: rec.id })}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">Aucune réclamation trouvée</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Delete confirmation dialog ── */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, single: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Confirmer la suppression
          <IconButton size="small" onClick={() => setDeleteConfirm({ open: false, single: null })}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {deleteConfirm.single !== null
              ? 'Êtes-vous sûr de vouloir supprimer cette réclamation ? Cette action est irréversible.'
              : `Êtes-vous sûr de vouloir supprimer les ${selectedIds.size} réclamations sélectionnées ? Cette action est irréversible.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirm({ open: false, single: null })} color="inherit">Annuler</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={bulkLoading}>
            {bulkLoading ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Details dialog ── */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Détails de la réclamation
              <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Étudiant</Typography>
                  <Typography variant="body1" fontWeight="500">{`${selected.lib_nom_pat_ind} ${selected.lib_pr1_ind}`}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Code étudiant</Typography>
                  <Typography variant="body1" fontWeight="500">{selected.cod_etu}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Module</Typography>
                  <Typography variant="body1" fontWeight="500">
                    {selected.module_name || selected.module_code} ({selected.module_code})
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Année / Semestre</Typography>
                  <Typography variant="body1" fontWeight="500">{selected.academic_year} / {selected.semester}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Note signalée</Typography>
                  <Typography variant="body1" fontWeight="500">
                    {selected.reported_grade === 'AB' ? 'Absent' : (selected.reported_grade ?? '0')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Session d'examen</Typography>
                  <Typography variant="body1" fontWeight="500">{selected.exam_session_name || '—'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Motif</Typography>
                  <Typography variant="body1">{selected.description}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                  <ExamSlotCard rec={selected} />
                </Grid>
                {selected.admin_response && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="subtitle2" color="text.secondary">Réponse de l'administration</Typography>
                    <Typography variant="body1">{selected.admin_response}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Date soumission</Typography>
                  <Typography variant="body1" fontWeight="500">{new Date(selected.created_at).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Statut</Typography>
                  <Chip
                    label={STATUS_META[selected.status]?.label || selected.status}
                    color={STATUS_META[selected.status]?.color || 'default'}
                    size="small" sx={{ fontWeight: 600 }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseDialog} color="inherit">Fermer</Button>
              <Button variant="outlined" color="error" startIcon={<DeleteIcon />}
                onClick={() => { handleCloseDialog(); setDeleteConfirm({ open: true, single: selected.id }); }}>
                Supprimer
              </Button>
              <Button variant="contained" onClick={() => openRespondDialog(selected)}>Répondre</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Respond dialog ── */}
      <Dialog open={respondDialog.open} onClose={() => setRespondDialog({ open: false, id: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Répondre à la réclamation
          <IconButton size="small" onClick={() => setRespondDialog({ open: false, id: null })}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Statut</InputLabel>
            <Select label="Statut" value={respondStatus} onChange={e => setRespondStatus(e.target.value)}>
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="in_progress">En cours</MenuItem>
              <MenuItem value="resolved">Traitée</MenuItem>
              <MenuItem value="rejected">Rejetée</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Réponse"
            multiline rows={4} fullWidth
            value={respondText}
            onChange={e => setRespondText(e.target.value)}
            placeholder="Réponse visible par l'étudiant..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRespondDialog({ open: false, id: null })} color="inherit">Annuler</Button>
          <Button variant="contained" onClick={handleSubmitResponse} disabled={actionLoading}>
            {actionLoading ? 'En cours...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(n => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotification(n => ({ ...n, open: false }))} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GradeReclamations;
